// eslint-disable-next-line no-restricted-imports
import { useLocation } from 'react-router'
import { useCallback, useMemo } from 'react'
import { useNavigateWithSearch } from 'infrastructure/routing'

export function useOverwriteSearchParams() {
  const { pathname } = useLocation()
  const navigate = useNavigateWithSearch()
  return useCallback(
    (newParams: Record<string, string>) => {
      const searchParams = new URLSearchParams(newParams)
      navigate({
        pathname,
        search: searchParams.toString(),
      })
    },
    [pathname, navigate],
  )
}

export function useSearchParams(): [
  URLSearchParams,
  (set: Record<string, string | number | undefined>) => void,
] {
  const { search, pathname } = useLocation()
  const navigate = useNavigateWithSearch()
  return useMemo(() => {
    const searchParams = new URLSearchParams(search)
    const setSearchParams = (
      newParams: Record<string, string | number | undefined>,
    ) => {
      Object.entries(newParams).forEach(([key, val]) => {
        if (val === undefined) {
          searchParams.delete(key)
        } else {
          searchParams.set(key, String(val))
        }
      })
      navigate({
        pathname,
        search: searchParams.toString(),
      })
    }
    return [searchParams, setSearchParams]
  }, [search, navigate, pathname])
}

export function useUrlSearchParam(
  paramKey: string,
): [string | undefined, (v: string | undefined) => void] {
  const [urlParams, setUrlParams] = useSearchParams()
  return useMemo(() => {
    return [
      urlParams.get(paramKey) ?? undefined,
      (newValue: string | undefined) => setUrlParams({ [paramKey]: newValue }),
    ]
  }, [paramKey, urlParams, setUrlParams])
}
